import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { config } from '@app/core/app-config';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { SignupChannel } from '@app/modules/user/signup/signup.interface';
import {
  LocalStorageKey,
  SegmentEvent,
  SignupUser,
  SignupV2Value,
  SignUpWithAirbnbCallback,
  SignUpWithAirbnbUrl,
} from '@app/shared/interfaces';
import { Rb2bService } from '@app/shared/services/rb2b.service';
import { SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { decodeToRealUserId } from '@app/shared/utils';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, delay, map, switchMap } from 'rxjs/operators';

const API_ROOT = `${config.API_URL}/signup`;
const API_ROOT_V2 = `${API_ROOT}/v2`;

@Injectable({ providedIn: 'root' })
export class SignupUserService {
  user$ = new BehaviorSubject<SignupUser | undefined>(undefined);
  preferredCountries$ = new BehaviorSubject<string[]>(['us']);

  constructor(
    private http: HttpClient,
    private rb2bService: Rb2bService,
    private segmentIoService: SegmentIoService,
    private authService: AuthenticationService
  ) {
    this.syncCountryCode();
    this.rb2bService.init();
  }

  private get authHeaders() {
    return this.user$.value?.token ? new HttpHeaders().set('Authorization', `Bearer ${this.user$.value.token}`) : null;
  }

  get isAirbnbSignup(): boolean {
    return localStorage.getItem(LocalStorageKey.isAirbnbSignUp) ? true : false;
  }

  validateEmail(email: string): Observable<SignupUser> {
    return this.http.get(`${API_ROOT_V2}/email/${email}`).pipe(
      map((res: { data: SignupUser }) => {
        if (res.data.status !== 'new') {
          this.segmentIoService.track(SegmentEvent.SEGMENT_ERROR_KEY, {
            status: res.data.status,
            message: '',
          });
        }

        return res.data;
      }),
      catchError((err) => {
        this.segmentIoService.track(SegmentEvent.SEGMENT_ERROR_KEY, {
          status: 'error',
          message: err.error?.errors?.email[0]
            ? err.error?.errors?.email[0]
            : 'We were unable to complete your signup at this time',
        });

        throw err;
      })
    );
  }

  createUser(request: {
    email: string;
    agreed_to_tos: boolean;
    flow: SignupV2Value;
    code?: string;
    referral_id?: string;
    sealed_result?: string;
  }) {
    return this.http.post(`${API_ROOT_V2}/email`, request).pipe(
      map((res: { data: SignupUser }) => {
        if (res.data.id) this.identifyWithSegment(res.data);
        return res.data;
      })
    );
  }

  updateName(firstName: string, lastName: string): Observable<SignupUser> {
    return this.http
      .post(`${API_ROOT_V2}/name`, { first_name: firstName, last_name: lastName }, { headers: this.authHeaders })
      .pipe(
        map((res: { data: SignupUser }) => {
          this.user$.next({ ...this.user$.value, ...res.data });
          return res.data;
        })
      );
  }

  updatePhone(phone: string): Observable<SignupUser> {
    return this.http.post(`${API_ROOT_V2}/phone`, { mobile_phone: phone }, { headers: this.authHeaders }).pipe(
      map((res: { data: SignupUser }) => {
        this.user$.next({ ...this.user$.value, ...res.data });
        return res.data;
      })
    );
  }

  validatePhone(code: string): Observable<SignupUser> {
    return this.http.post(`${API_ROOT_V2}/phone/verify`, { code }, { headers: this.authHeaders }).pipe(
      map((res: { data: SignupUser }) => {
        this.user$.next({ ...this.user$.value, ...res.data });
        return res.data;
      })
    );
  }

  requestNewOtp(): Observable<boolean> {
    return this.http.post(`${API_ROOT_V2}/phone/send`, {}, { headers: this.authHeaders }).pipe(map(() => true));
  }

  updatePropertyCount(properties: number): Observable<SignupUser> {
    return this.http
      .post(`${API_ROOT_V2}/properties`, { property_count: properties }, { headers: this.authHeaders })
      .pipe(
        map((res: { data: SignupUser }) => {
          this.user$.next({ ...this.user$.value, ...res.data });
          return res.data;
        })
      );
  }

  updatePlatforms(platforms: string[]): Observable<SignupUser> {
    return this.http.post(`${API_ROOT_V2}/platforms`, { platforms }, { headers: this.authHeaders }).pipe(
      map((res: { data: SignupUser }) => {
        this.user$.next({ ...this.user$.value, ...res.data });
        return res.data;
      })
    );
  }

  updatePassword(body: { password: string; password_confirmation: string }): Observable<SignupUser> {
    return this.http.post(`${API_ROOT_V2}/password`, body, { headers: this.authHeaders }).pipe(
      map((res: { data: SignupUser }) => {
        this.user$.next({ ...this.user$.value, ...res.data });
        return res.data;
      })
    );
  }

  updateOnboardingFeatures(onboarding: string[]): Observable<SignupUser> {
    return this.http.post(`${API_ROOT_V2}/onboarding`, { onboarding }, { headers: this.authHeaders }).pipe(
      map((res: { data: SignupUser }) => {
        this.user$.next({ ...this.user$.value, ...res.data });
        return res.data;
      })
    );
  }

  signUpWithAirbnb(): Observable<SignUpWithAirbnbUrl> {
    return this.http
      .get(`${API_ROOT_V2}/airbnb/authorization/url`)
      .pipe(map((res: { data: SignUpWithAirbnbUrl }) => res.data));
  }

  signUpWithAirbnbCallback(code: string, state: string, flow: SignupV2Value): Observable<SignUpWithAirbnbCallback> {
    return this.http
      .post(`${API_ROOT_V2}/airbnb/authorization/callback`, { code, state, flow })
      .pipe(map((res: { data: SignUpWithAirbnbCallback }) => res.data));
  }

  completeSignup(redirectUrl = this.getRedirectUrl()): Observable<void> {
    return this.http.post(`${API_ROOT_V2}/complete`, null, { headers: this.authHeaders }).pipe(
      map((res: { data: SignupUser }) => {
        this.user$.next({ ...this.user$.value, ...res.data });

        const token = this.user$.value?.token;
        if (!token) throw new Error('No token found');

        localStorage.setItem('accessToken', token);
        this.authService.isLoggedInSubject.next(true);
      }),
      delay(100),
      switchMap(() => this.authService.getUserDetailsFromApi()),
      map((res) => {
        if (res.error) {
          this.segmentIoService.track(SegmentEvent.SignupUserApiFailedToLoad, {});
          throw new Error('Failed to get user details');
        }

        localStorage.setItem('user', JSON.stringify(res));
        this.segmentIoService.track(SegmentEvent.AccountCreated, {
          label: 'Trial',
          isAirbnbSignUp: this.isAirbnbSignup,
        });

        this.authService.loginActions(redirectUrl);

        this.reset();

        return;
      })
    );
  }

  hasChannel(channel: SignupChannel) {
    return this.user$.value?.platforms?.includes(channel);
  }

  private reset() {
    this.user$.next(undefined);
    this.preferredCountries$.next(['us']);
  }

  private getRedirectUrl() {
    let redirectUrl = config.ONBOARDING_PAGE;

    if (this.hasChannel(SignupChannel.Airbnb)) {
      redirectUrl = this.isAirbnbSignup ? 'onboarding' : 'accounts/new/airbnb';
    } else if (this.hasChannel(SignupChannel.BookingDotCom)) {
      redirectUrl = 'accounts/new/booking';
    } else if (this.hasChannel(SignupChannel.Vrbo)) {
      redirectUrl = 'accounts/new/vrbo';
    }

    return redirectUrl;
  }

  private syncCountryCode() {
    this.user$.subscribe((user) => {
      if (user && user.country_code && user.country_code.length === 2 && user.country_code.toLowerCase() !== 'us') {
        this.preferredCountries$.next([user.country_code.toLowerCase(), 'us']);
      } else {
        this.preferredCountries$.next(['us']);
      }
    });
  }

  private identifyWithSegment(user: SignupUser): void {
    const user_id = decodeToRealUserId(user.id, user.email) ?? '0';

    this.segmentIoService.identify(
      user_id,
      {
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        phone: user.mobile_phone,
        picture: user.avatar,
      },
      {
        additional_signup_flow: user.flow,
      }
    );
  }
}
